import { useAuth } from "@/App";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  LogOut,
  Users,
  UserPlus,
  Settings,
  UserCircle,
  ArrowRight,
  Search,
  Building2
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

const DashboardPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const menuCards = [
    {
      id: "bni-search",
      title: "BNI Search",
      description: "Cerca e gestisci i membri dei capitoli BNI. Analizza i dati SEO e invia comunicazioni marketing.",
      icon: Users,
      color: "bg-blue-500",
      hoverColor: "hover:bg-blue-600",
      link: "/chapters",
      testId: "bni-search-card"
    },
    {
      id: "nuovi-lead",
      title: "Nuovi Lead",
      description: "Cerca nuove attività su Google My Business. Estrai contatti e analizza i dati per acquisire nuovi clienti.",
      icon: UserPlus,
      color: "bg-orange-500",
      hoverColor: "hover:bg-orange-600",
      link: "/leads",
      testId: "nuovi-lead-card"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100" data-testid="dashboard-page">
      {/* Header */}
      <header className="sticky top-0 z-50 w-full border-b bg-white/80 backdrop-blur-md">
        <div className="container mx-auto px-4 flex h-16 items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="https://maxvalle.it/wp-content/uploads/2026/01/verticale-arancio-scuro-2.png" 
              alt="Max Valle Logo" 
              className="h-10 w-auto"
            />
            <div>
              <h1 className="text-lg font-bold tracking-tight">Marketing Max Valle</h1>
              <p className="text-xs text-muted-foreground hidden sm:block">Lead Generation Platform</p>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <Link to="/settings">
              <Button variant="outline" size="sm" className="btn-animate" data-testid="settings-button">
                <Settings className="h-4 w-4 mr-2" />
                Impostazioni
              </Button>
            </Link>
            <div className="hidden sm:flex items-center gap-2 text-sm text-muted-foreground">
              <UserCircle className="h-4 w-4" />
              <span>{user?.name}</span>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={logout}
              className="btn-animate"
              data-testid="logout-button"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Esci
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-12">
        {/* Welcome Section */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-3">
            Benvenuto, {user?.name?.split(' ')[0] || 'Utente'}
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Seleziona uno strumento per iniziare a generare lead e gestire le tue campagne marketing.
          </p>
        </motion.div>

        {/* Menu Cards */}
        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {menuCards.map((card, index) => (
            <motion.div
              key={card.id}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.15 }}
            >
              <Card 
                className="group cursor-pointer border-2 border-transparent hover:border-slate-200 transition-all duration-300 hover:shadow-xl overflow-hidden"
                onClick={() => navigate(card.link)}
                data-testid={card.testId}
              >
                <CardHeader className="pb-4">
                  <div className={`w-14 h-14 rounded-xl ${card.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                    <card.icon className="h-7 w-7 text-white" />
                  </div>
                  <CardTitle className="text-2xl font-bold text-slate-800 group-hover:text-slate-900">
                    {card.title}
                  </CardTitle>
                  <CardDescription className="text-base text-slate-600">
                    {card.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center text-sm font-medium text-slate-500 group-hover:text-slate-700 transition-colors">
                    <span>Accedi</span>
                    <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Quick Stats or Info */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="mt-16 text-center"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-sm border">
            <Building2 className="h-4 w-4 text-slate-500" />
            <span className="text-sm text-slate-600">
              Piattaforma per la generazione e gestione lead
            </span>
          </div>
        </motion.div>
      </main>
    </div>
  );
};

export default DashboardPage;
